import { gql } from '@apollo/client';
import { BlockRenderer } from '~/components/DatoCms/BlockRenderer';
import DatoMetaTags from '~/components/DatoCms/DatoMetaTags';
import NavBarblock from '~/components/_cmsBlocks/NavbarBlock';
import config from '~/config';
import type {
  fetchCampaignBySlug,
  fetchCampaignBySlugVariables,
  fetchCampaignBySlug_allCampaigns,
} from '~/types/generated/cms/fetchCampaignBySlug';
import type { getAllCampaignSlugs } from '~/types/generated/cms/getAllCampaignSlugs';
import { GET_ALL_CAMPAIGN_SLUGS } from 'src/datoCMS/queries';
import {
  CampaignContainerFragment,
  ColorFragment,
  SeoMetaTagsFragment,
} from 'src/datoCMS/fragments/fragments';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import type { GetStaticProps } from 'next';

const FETCH_CAMPAIGN_BY_SLUG = gql`
  ${SeoMetaTagsFragment}
  ${CampaignContainerFragment}
  ${ColorFragment}
  query fetchCampaignBySlug($slug: String) {
    allCampaigns(filter: { slug: { eq: $slug } }) {
      hideHeaderAndFooter
      pageName
      id
      slug
      seo: _seoMetaTags {
        ...SeoMetaTagsFragment
      }
      noIndex
      navbar {
        id
        hideDebLogo
        logo {
          url
        }
        logoPosition
        url
      }
      container {
        id
        heading
        bgColor {
          ...ColorFragment
        }
        campaignContainerContent {
          ...CampaignContainerFragment
        }
      }
    }
  }
`;

interface CampaignPageProps {
  allCampaigns: fetchCampaignBySlug_allCampaigns;
}

const CampaignPage: React.FC<CampaignPageProps> = ({ allCampaigns }) => {
  const canonicalUrl = `https://energiebespaarders.nl/campagne/${allCampaigns.slug}`;

  return (
    <>
      <DatoMetaTags
        metaTags={allCampaigns.seo}
        noIndex={allCampaigns.noIndex}
        {...(allCampaigns.noIndex ? {} : { canonicalUrl })}
      />
      {allCampaigns.navbar[0] !== undefined && <NavBarblock block={allCampaigns.navbar[0]} />}
      {allCampaigns.container.map(block => {
        return <BlockRenderer key={block.id} block={block as any} />;
      })}
    </>
  );
};

export default CampaignPage;

export const getStaticProps: GetStaticProps<CampaignPageProps> = async ({ params, preview }) => {
  const client = getDatoClient(preview);

  const { data } = await client.query<fetchCampaignBySlug, fetchCampaignBySlugVariables>({
    query: FETCH_CAMPAIGN_BY_SLUG,
    variables: { slug: params?.slug as string },
    fetchPolicy: 'network-only',
  });

  if (!data.allCampaigns.length) {
    return {
      redirect: { destination: '/', permanent: false },
      revalidate: 60,
    };
  }

  const navAndFooter = data.allCampaigns[0].hideHeaderAndFooter
    ? { hideNav: true, hideFooter: true }
    : {};

  return {
    props: { allCampaigns: data.allCampaigns[0], ...navAndFooter },
    revalidate: 60,
  };
};

export const getStaticPaths = async () => {
  if (config.build.skipStaticGeneration) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const client = getDatoClient();

  const { data } = await client.query<getAllCampaignSlugs>({ query: GET_ALL_CAMPAIGN_SLUGS });

  return {
    paths: data.allCampaigns.map(s => ({ params: { slug: s.slug } })),
    fallback: 'blocking',
  };
};
